import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from 'react';

// DOM helpers shared by ScanCountDialog and ReportProblemDialog.

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** The container's tabbable controls in document order (hidden ones left out). */
export function tabbableIn(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) => element.tabIndex >= 0 && element.getClientRects().length > 0,
  );
}

/**
 * Keeps Tab and Shift+Tab inside a dialog: past the last control comes the first, and back.
 * Works for a key event inside the dialog and for one that fell out of it onto the page.
 */
export function trapTabKey(event: ReactKeyboardEvent | KeyboardEvent, container: HTMLElement): void {
  if (event.key !== 'Tab' || event.defaultPrevented) return;
  const items = tabbableIn(container);
  if (!items.length) {
    event.preventDefault();
    container.focus({ preventScroll: true });
    return;
  }
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;
  const inside = active instanceof Node && container.contains(active);
  if (event.shiftKey) {
    if (!inside || active === first || active === container) {
      event.preventDefault();
      last.focus();
    }
  } else if (!inside || active === last) {
    event.preventDefault();
    first.focus();
  }
}

/** The element focused before a dialog opened. Read it during the first render, before autofocus moves it. */
export function captureOpener(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const active = document.activeElement;
  return active instanceof HTMLElement && active !== document.body ? active : null;
}

/** A press on the backdrop keeps focus where it was (and never closes the dialog, which holds a count). */
export function holdFocusOnBackdrop(event: ReactMouseEvent<HTMLElement>): void {
  if (event.target === event.currentTarget) event.preventDefault();
}

/** The scanner field inside the dialog (PickingScanInput's input), when Scan mode shows it. */
export function findScanField(container: HTMLElement | null): HTMLInputElement | null {
  const field = container?.querySelector<HTMLInputElement>('.picking-scan-input__field') ?? null;
  return field && !field.disabled ? field : null;
}

function matches(query: string): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
}

/** A key that types a character (Space excluded: it presses buttons and chips). */
export function isTypingKey(event: { key: string; ctrlKey: boolean; metaKey: boolean; altKey: boolean }): boolean {
  return event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey;
}

const NON_TEXT_INPUTS = ['button', 'checkbox', 'color', 'file', 'image', 'radio', 'range', 'reset', 'submit'];

/** The target takes typed text itself (a field, a select, an editable region). */
export function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true;
  return target instanceof HTMLInputElement && !NON_TEXT_INPUTS.includes(target.type);
}

/** Keys that reached the page itself: focus fell out of the dialog (its control was disabled or removed). */
export function isStrayKey(event: KeyboardEvent): boolean {
  return !event.defaultPrevented && (event.target === document.body || event.target === document.documentElement);
}

/**
 * Scrolls a line's row into view in the two-column layout, where the lines scroll on their own.
 * In the one-column layout (phones, narrow tablets) it does nothing, so the scanner stays put.
 */
export function revealLineRow(container: HTMLElement | null, lineId: string): void {
  if (!container || !matches('(min-width: 721px)')) return;
  for (const row of Array.from(container.querySelectorAll<HTMLElement>('[data-line-id]'))) {
    if (row.dataset.lineId === lineId) {
      row.scrollIntoView({ block: 'nearest' });
      return;
    }
  }
}
